import type { ProductListItem } from '../../lib/site';

/**
 * 가격 표기 — 카드·리스트·상세가 **같이 쓴다**.
 *
 * 현행 `product_list_inc.php` 를 그대로 옮겼다.
 *  - 단가는 상품이 파는 종류(`salePlatforms`)마다 한 줄이다 — 공용(ALL)·PC·모바일.
 *    각 종류가 자기 정가/할인가 칸을 따로 갖는다(현행 ori_price/ori_price_pc/ori_price_m).
 *  - **단가 세분화를 끈 마켓은 첫 줄 하나만** 쓴다(현행 `if(price_detail_yn=='N') $sale_prd = [$sale_prd[0]]`).
 *  - 할인 표기는 정액이면 `-10,000원`, 정률이면 `↓10%`(현행 dc_type A/R).
 */

/** 단가 한 줄 */
export interface PriceLine {
  platform: 'ALL' | 'PC' | 'MOBILE';
  /** 정가 — 할인이 없으면 `price` 와 같다 */
  original: number;
  /** 실제 파는 값 */
  price: number;
}

const PRICE_BY_PLATFORM: Record<string, (p: ProductListItem) => PriceLine> = {
  ALL: (p) => ({ platform: 'ALL', original: p.priceOriginal, price: p.price }),
  PC: (p) => ({ platform: 'PC', original: p.priceOriginalPc, price: p.pricePc }),
  MOBILE: (p) => ({ platform: 'MOBILE', original: p.priceOriginalMobile, price: p.priceMobile }),
};

/**
 * 그릴 단가 줄들.
 * @param priceDetail 설정>기능옵션 '강좌 단가 세분화'. 끄면 첫 줄만
 */
export function priceLines(p: ProductListItem, priceDetail: boolean): PriceLine[] {
  const known = p.salePlatforms.filter((x) => x in PRICE_BY_PLATFORM);
  // 파는 종류가 하나도 안 잡힌 상품 — 공용 단가로 본다(현행도 기본이 1이다)
  const platforms = known.length ? known : ['ALL'];
  const lines = platforms.map((x) => PRICE_BY_PLATFORM[x](p));
  /*
   * 세분화를 끈 마켓은 **파는 첫 줄 하나**다 — 여기서 순서를 바꾸면 파는 값이 달라지므로
   * 자르기 전에는 손대지 않는다(현행 `$sale_prd = [$sale_prd[0]]` 와 같은 자리).
   */
  if (!priceDetail) return lines.slice(0, 1);
  // 보이는 차례는 늘 공용 → PC → 모바일(2026-08-26 사용자 요청) — DB 에 담긴 차례는 제각각이다
  return [...lines].sort((x, y) => LINE_RANK.indexOf(x.platform) - LINE_RANK.indexOf(y.platform));
}

const LINE_RANK: PriceLine['platform'][] = ['ALL', 'PC', 'MOBILE'];

/** 천단위 구분 — 금액은 늘 이 함수를 지난다 */
export const won = (n: number) => n.toLocaleString('ko-KR');

/**
 * 할인 표기(기호 없음) — 없으면 `null`. 정액은 깎이는 금액, 정률은 퍼센트.
 * **상세 구매 박스가 쓴다** — 가이드 `pd-price-discount` 가 `10%` 처럼 기호 없이 적는다.
 */
export function discountShort(p: ProductListItem, moneyUnit: string): string | null {
  if (p.discountType === 'NONE' || p.discountPrice <= 0) return null;
  return p.discountType === 'AMOUNT' ? `-${won(p.discountPrice)}${moneyUnit}` : `${p.discountPrice}%`;
}

/**
 * 할인 표기(목록 카드) — 정률 앞에 `↓` 가 붙는다. **현행 표기 그대로다**.
 * 상세와 문구가 갈리지 않게 위 함수를 감싼다 — 다른 것은 기호뿐이다.
 */
export function discountLabel(p: ProductListItem, moneyUnit: string): string | null {
  const short = discountShort(p, moneyUnit);
  return short && p.discountType !== 'AMOUNT' ? `↓${short}` : short;
}

/**
 * 지금 담기·구매를 할 수 있나.
 * 판매중이어야 하고, 신청기간을 쓰는 상품이면 그 기간 안이어야 하며(§2-1-1),
 * 교재는 **재고가 있어야** 한다 — 노출은 되는데 구매만 막힌 상태가 실제로 있다.
 */
export const buyable = (p: ProductListItem) =>
  p.saleStatus === 'ON_SALE' && p.applyOpen && !p.soldOut;

/**
 * 살 수 없을 때 **버튼에 적을 말** — 가격은 그대로 두고 버튼만 바꾼다(2026-09-02 사용자 지시).
 *
 * 품절은 `statusLabel` 과 달리 가격을 가리지 않는다. 값은 그대로인데 지금 못 살 뿐이다.
 * 다른 사유(판매예정·종료·신청마감)는 이미 `statusLabel` 이 가격 자리째 바꾼다.
 */
export function blockedLabel(
  p: ProductListItem,
  t: (k: string) => string,
): string | null {
  return p.soldOut ? t('prd.status.soldOut') : null;
}

/**
 * 가격 자리에 넣을 상태 문구 — 살 수 없을 때만 있다.
 * 가이드가 이 자리에 `course-price soon` 같은 상태 표시를 둔다.
 *
 * **품절은 여기 넣지 않는다**(2026-09-02 사용자 지시 — "가격은 그대로 보이게, 버튼만") —
 * 값이 안 바뀌었는데 가격을 지울 까닭이 없다. 품절은 `buyLabel()` 이 버튼에서만 말한다.
 */
export function statusLabel(
  p: ProductListItem,
  t: (k: string) => string,
): { text: string; className: string } | null {
  if (p.saleStatus === 'SCHEDULED') return { text: t('prd.status.scheduled'), className: 'soon' };
  if (p.saleStatus === 'ENDED') return { text: t('prd.status.ended'), className: 'ended' };
  if (!p.applyOpen) return { text: t('prd.status.applyClosed'), className: 'soon' };
  return null;
}

/**
 * 수강 제한 배지 문구 — 현행 카드의 회색 배지들.
 * 누적/차시별 각각 "n배"(배수) 또는 "n시간 n분"(고정)으로 적는다.
 */
/**
 * 수강 제한 문구 — **짧은 것과 풀어 쓴 것을 함께** 만든다(2026-08-23 사용자 지적
 * "저렇게 해놓으면 뭔 뜻인지 사용자가 알까?").
 *
 * 같은 값이라도 자리마다 필요한 길이가 다르다 —
 *  - `compact` : 목록 카드의 회색 배지. 가이드가 `2배`·`2시간` 처럼 짧게 쓴다
 *  - `long`    : 상세 스펙 칸의 부제. 자리가 있으니 **뜻이 통하게** 쓴다
 *    (`2배` → `영상 길이의 2배까지 시청`)
 *
 * 두 문구를 한 함수에서 만든다 — 자리마다 따로 지으면 같은 값이 다른 뜻으로 갈린다.
 *
 * 갈래는 셋이고 상세는 관계 있는 칸에 나눠 녹인다 —
 *  - `lecture` 누적 수강 가능 시간 → **수강기간** 칸
 *  - `lesson`  차시별 제한 → **수강차시** 칸
 *  - `daily`   1일 제한 → 같은 수강차시 칸
 */
export interface LimitText { compact: string; long: string }

export function limitParts(
  p: Pick<ProductListItem, 'studyLimit'>,
  t: (k: string, v?: Record<string, string | number>) => string,
): { lecture: LimitText | null; lesson: LimitText | null; daily: LimitText[] } {
  const limit = p.studyLimit ?? {};
  /**
   * 값 문구 — `2배` 또는 시간.
   * **배지는 시간까지만** 적는다(2026-08-23 사용자 지시 — 분은 버린다. 반올림이 아니다).
   * 상세 부제는 분까지 그대로 적는다 — 자리가 있고, 정확한 값이 필요한 자리다.
   */
  const value = (l: { mode: 'RELATIVE' | 'FIXED'; times?: number; seconds?: number } | undefined, compact: boolean) => {
    if (!l) return null;
    if (l.mode === 'RELATIVE') return l.times ? t('prd.limit.times', { n: l.times }) : null;
    if (!l.seconds) return null;
    const h = Math.floor(l.seconds / 3600);
    const m = Math.floor((l.seconds % 3600) / 60);
    if (compact) return h ? t('prd.limit.hour', { h }) : t('prd.limit.min', { m });
    return h && m ? t('prd.limit.hourMin', { h, m }) : h ? t('prd.limit.hour', { h }) : t('prd.limit.min', { m });
  };
  const make = (
    l: { mode: 'RELATIVE' | 'FIXED'; times?: number; seconds?: number } | undefined,
    relKey: string, fixKey: string,
  ): LimitText | null => {
    const compact = value(l, true);
    if (!compact || !l) return null;
    // 배수는 **숫자를 그대로** 넘긴다 — 짧은 표기(`2배`)를 긴 문구에 끼우면 `2배수 제한` 같은
    // 말을 만들 수 없다. 단위는 언어마다 다르므로 번역 문구가 정한다.
    const full = value(l, false) ?? compact;
    return { compact, long: t(l.mode === 'RELATIVE' ? relKey : fixKey, { v: full, n: l.times ?? 0 }) };
  };

  return {
    lecture: make(limit.lecture, 'prd.limit.long.totalTimes', 'prd.limit.long.totalFixed'),
    lesson: make(limit.lesson, 'prd.limit.long.lessonTimes', 'prd.limit.long.lessonFixed'),
    daily: [
      limit.daily?.lessons
        ? { compact: t('prd.limit.dailyLessons', { n: limit.daily.lessons }), long: t('prd.limit.long.dailyLessons', { n: limit.daily.lessons }) }
        : null,
      limit.daily?.lectures
        ? { compact: t('prd.limit.dailyLectures', { n: limit.daily.lectures }), long: t('prd.limit.long.dailyLectures', { n: limit.daily.lectures }) }
        : null,
    ].filter((x): x is LimitText => x != null),
  };
}

export function limitBadges(p: ProductListItem, t: (k: string, v?: Record<string, string | number>) => string): string[] {
  /*
   * `studyLimit` 이 **없을 수도 있다**고 보고 읽는다(2026-08-22 사용자 로그로 교정).
   * 서버가 정규화해 주지만, 한 칸이 비었다고 상세 페이지 전체가 죽는 것은 과하다 —
   * 실제로 상세가 날것(`null`)을 주던 동안 이 줄에서 SSR 이 끊겨 본문이 안 나왔다.
   */
  /* 목록 배지는 **짧은 쪽**을 쓴다(가이드가 `2배`·`2시간` 처럼 쓴다) */
  const parts = limitParts(p, t);
  return [parts.lecture, parts.lesson, ...parts.daily].filter((x): x is LimitText => x != null).map((x) => x.compact);
}
